import { useState, useRef } from 'react';
import { Modal, Button, Form, Alert, Table, ProgressBar } from 'react-bootstrap';
import { Upload } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useOMS } from '../../../../apps/hooks/oms/useOMS';
import { useAuth } from '../../../../providers/AuthProvider';
import { parseTrackingCSV } from '../utils/trackingImport';
import type { TrackingImportRow } from '../utils/trackingImport';

interface ImportTrackingModalProps {
  readonly show: boolean;
  readonly onHide: () => void;
}

interface ImportProgress {
  total: number;
  processed: number;
  succeeded: number;
  failed: number;
  errors: string[];
}

export function ImportTrackingModal({ show, onHide }: ImportTrackingModalProps) {
  const [csvData, setCsvData] = useState<TrackingImportRow[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [previewData, setPreviewData] = useState<TrackingImportRow[]>([]);
  const [updateTracking, setUpdateTracking] = useState(true);
  const [updateStatus, setUpdateStatus] = useState(true);
  const [isImporting, setIsImporting] = useState(false);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const oms = useOMS();
  const { user } = useAuth();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setParseErrors([]);
    setCsvData([]);
    setPreviewData([]);
    setProgress(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const csvText = e.target?.result as string;
      const { data, errors } = parseTrackingCSV(csvText);

      setCsvData(data);
      setParseErrors(errors);
      setPreviewData(data.slice(0, 10));
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (csvData.length === 0 || (!updateTracking && !updateStatus)) return;

    setIsImporting(true);
    const importProgress: ImportProgress = {
      total: csvData.length,
      processed: 0,
      succeeded: 0,
      failed: 0,
      errors: [],
    };
    setProgress(importProgress);

    const operatorName = user?.name || user?.email || 'Unknown';

    for (const row of csvData) {
      try {
        if (updateTracking) {
          await oms.updateOrder(row.orderId, {
            shippingData: { trackingNumber: row.trackingNumber },
            auditEntry: {
              operator: operatorName,
              source: 'OPERATOR',
            },
          });
        }

        if (updateStatus && row.status) {
          await oms.updateOrderStatus(row.orderId, row.status, operatorName);
        }

        importProgress.succeeded++;
      } catch (error) {
        importProgress.failed++;
        const message = error instanceof Error ? error.message : 'Unknown error';
        importProgress.errors.push(`Order ${row.orderId}: ${message}`);
      }

      importProgress.processed++;
      setProgress({ ...importProgress });
    }

    setIsImporting(false);

    if (importProgress.failed === 0) {
      toast.success(`${importProgress.succeeded} orders updated successfully`);
    } else {
      toast.error(`${importProgress.failed} of ${importProgress.total} orders failed`);
    }
  };

  const handleClose = () => {
    if (isImporting) return;
    setCsvData([]);
    setParseErrors([]);
    setPreviewData([]);
    setProgress(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onHide();
  };

  const progressPercent = progress ? Math.round((progress.processed / progress.total) * 100) : 0;
  const isImportDone = progress !== null && progress.processed === progress.total && !isImporting;

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <Upload size={20} />
          Import Tracking Data
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Select CSV File</Form.Label>
          <Form.Control
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            disabled={isImporting}
          />
          <Form.Text className="text-muted">
            Expected format: Order ID, Country Code, Tracking Number, Status
          </Form.Text>
        </Form.Group>

        {parseErrors.length > 0 && (
          <Alert variant="danger">
            <Alert.Heading>Validation Errors</Alert.Heading>
            <ul className="mb-0">
              {parseErrors.slice(0, 10).map((error, index) => (
                <li key={index}>{error}</li>
              ))}
              {parseErrors.length > 10 && <li>... and {parseErrors.length - 10} more errors</li>}
            </ul>
          </Alert>
        )}

        {csvData.length > 0 && parseErrors.length === 0 && !progress && (
          <Alert variant="success">
            <strong>{csvData.length}</strong> orders ready to import.
          </Alert>
        )}

        {csvData.length > 0 && parseErrors.length === 0 && !progress && (
          <div className="mb-3">
            <Form.Label className="fw-bold">Update Options</Form.Label>
            <Form.Check
              type="checkbox"
              id="update-tracking"
              label="Update Tracking Number"
              checked={updateTracking}
              onChange={(e) => setUpdateTracking(e.target.checked)}
            />
            <Form.Check
              type="checkbox"
              id="update-status"
              label="Update Order Status"
              checked={updateStatus}
              onChange={(e) => setUpdateStatus(e.target.checked)}
            />
          </div>
        )}

        {previewData.length > 0 && !progress && (
          <div className="mt-3">
            <h6>Preview{csvData.length > 10 ? ` (first 10 of ${csvData.length})` : ''}:</h6>
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Country</th>
                  <th>Tracking Number</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {previewData.map((row, index) => (
                  <tr key={index}>
                    <td>{row.orderId}</td>
                    <td>{row.countryCode}</td>
                    <td><code>{row.trackingNumber}</code></td>
                    <td>{row.status}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {progress && (
          <div className="mt-3">
            <ProgressBar
              now={progressPercent}
              label={`${progress.processed} / ${progress.total}`}
              animated={isImporting}
              variant={progress.failed > 0 ? 'warning' : 'primary'}
            />
            <div className="d-flex gap-3 mt-2 small">
              <span className="text-success">Succeeded: {progress.succeeded}</span>
              {progress.failed > 0 && <span className="text-danger">Failed: {progress.failed}</span>}
            </div>
            {progress.errors.length > 0 && (
              <Alert variant="danger" className="mt-2">
                <ul className="mb-0 small">
                  {progress.errors.slice(0, 10).map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                  {progress.errors.length > 10 && (
                    <li>... and {progress.errors.length - 10} more errors</li>
                  )}
                </ul>
              </Alert>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isImporting}>
          {isImportDone ? 'Close' : 'Cancel'}
        </Button>
        {!isImportDone && (
          <Button
            variant="primary"
            onClick={handleImport}
            disabled={csvData.length === 0 || parseErrors.length > 0 || isImporting || (!updateTracking && !updateStatus)}
          >
            {isImporting ? 'Importing...' : `Import ${csvData.length} Orders`}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
